import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import { classifyHomeBoxText, trimChatHistory, type ActorDTO, type BridgeView, type DecisionDTO, type EventDTO, type SteerItem, type Stage } from "@roomate/core";
import { STEER_REASON_LABEL } from "../lib/steer.ts";
import { chiefToday, chiefTileText } from "../lib/chief.ts";
import { ChiefNote } from "../components/ChiefNote.tsx";
import { api, useActors, useAnswer, useBridge, useChiefToday, useCreate, useEvents, useMe, useProject, useProjects, useRun, useSandboxStatus, useStopRun } from "../api.ts";
import { needsReconnect } from "../lib/sandbox.ts";
import { ReconnectItem } from "./SandboxPanel.tsx";
import { useLive } from "../live.tsx";
import { AnimatedList } from "../components/ui/animated-list.tsx";
import { useCurrentProject } from "../project.tsx";
import { useMedia } from "../lib/use-media.ts";
import { delegateBlockView, type DelegateBlockView } from "../lib/delegation.ts";
import { NEEDS_RATIONALE } from "./Inbox.tsx";
import { money } from "./Run.tsx";
import { Ticker } from "../components/ui/number-ticker.tsx";
import { BUDGET_STATE_LABEL, BUDGET_TONE, budgetFor, budgetLine } from "../lib/mission.ts";
import { MissionGrid } from "../components/MissionGrid.tsx";
import { Symbol } from "../components/Logo.tsx";
import { answeredByLine, homeChatPollMs, sourceKind, type HomeChat } from "../lib/home-chat.ts";
import { StarterTeamCard } from "../components/StarterTeamCard.tsx";
import { NoWorkersBanner, useNoWorkerReason } from "../components/NoWorkersBanner.tsx";
import { DecisionCrumb } from "../components/DecisionCrumb.tsx";
import { historyName, paletteActors } from "../roster.ts";
import { Ago, cx, describeEvent, ErrorNote, formatMin, Markdown, useNames } from "../ui.tsx";

/*
 * Home. One calm screen: what time it is, what needs you (one thing at a time),
 * who is working, and a place to ask the office or start something new. From lg up
 * it is a bento grid as wide as the top bar: "Needs you" large, "Ask the office" a
 * tall card beside it as high as the cards it sits next to, then project, today and
 * live cards; the agents run full width below. Below lg the cards stack and the chat
 * is a bar at the bottom. Everything else lives one click away.
 */

/** One bento card: same glass, radius and padding everywhere on Home. */
const CARD = "glass rounded-[24px] p-5 md:p-6";

const VERB: Record<string, string> = { human_action: "Act", review_change: "Approve", approve_spec: "Approve", spot_check: "Check", question: "Advise", choose_option: "Advise" };

/** Terminal output can carry ANSI colour codes; show text, not escape sequences. */
const stripAnsi = (s: string) => s.replace(/\u001b\[[0-9;?]*[A-Za-z]/g, "").replace(/\[[0-9;]{1,12}m/g, "");


function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

type Item = { kind: "decision"; d: DecisionDTO } | { kind: "steer"; s: SteerItem };

export function homeAttentionCounts(needsAHuman: { act: readonly unknown[]; approve: readonly unknown[]; advise: readonly unknown[]; steer: readonly unknown[] } | undefined) {
  if (!needsAHuman) return { decisions: 0, steer: 0 };
  return {
    decisions: needsAHuman.act.length + needsAHuman.approve.length + needsAHuman.advise.length,
    steer: needsAHuman.steer.length,
  };
}

export function BridgePage() {
  const { projectId, project, ready } = useCurrentProject();
  // Wait for the project list so the first fetch is already scoped.
  const bridge = useBridge(projectId || undefined, ready);
  const me = useMe().data!;
  const now = useNow(1000);
  const data = bridge.data;

  const items: Item[] = useMemo(() => {
    if (!data) return [];
    const n = data.needsAHuman;
    return [...[...n.act, ...n.approve, ...n.advise].map((d): Item => ({ kind: "decision", d })), ...n.steer.map((s): Item => ({ kind: "steer", s }))];
  }, [data]);
  const lanes = data?.agentLanes ?? [];
  const { decisions: decisionCount, steer: steerCount } = homeAttentionCounts(data?.needsAHuman);

  const wide = useMedia("(min-width: 1024px)");

  const hour = new Date(now).getHours();
  const greeting = hour < 5 ? "Good night" : hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  return (
    <div className="mx-auto max-w-[1180px] pb-40 pt-6 md:pt-10 lg:pb-16" data-testid="bridge">
      {/* Time and one sentence. The only big type on the screen. */}
      <header className="os-rise">
        <p className="text-[13px] text-ink-2">
          {new Date(now).toLocaleDateString([], { weekday: "long", day: "numeric", month: "long" })} ·{" "}
          <span className="tabular-nums">{new Date(now).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false })}</span>
        </p>
        <h1 className="mt-2 text-[40px] font-semibold leading-[1.05] tracking-[-0.035em] md:text-[52px]">
          {greeting}, {me.name}.
        </h1>
        <p className="mt-3 text-[17px] text-ink-2" data-testid="bridge-summary">
          {bridge.isError ? (
            "Can't reach the hub right now — what you see may be out of date."
          ) : !data ? (
            "Looking around…"
          ) : (
            <>
              {decisionCount === 0 ? (
                "No decisions waiting"
              ) : (
                <span className="text-ink">
                  <Ticker value={decisionCount} className="tabular-nums" /> {decisionCount === 1 ? "decision" : "decisions"} for you
                </span>
              )}
              {steerCount > 0 && (
                <>
                  {" · "}
                  <Ticker value={steerCount} className="tabular-nums" /> to keep an eye on
                </>
              )}
              {" · "}
              {lanes.length === 0 ? (
                "no agents working"
              ) : (
                <>
                  <Ticker value={lanes.length} className="tabular-nums" /> {lanes.length === 1 ? "agent is" : "agents are"} working
                </>
              )}
            </>
          )}
        </p>
        {bridge.isError && <ErrorNote error={bridge.error} />}
      </header>

      <StarterTeamCard />

      <NoWorkersBanner className="os-rise mt-10" />

      <div className="os-rise mt-10 grid grid-cols-1 gap-4 lg:grid-cols-3" style={{ ["--i" as string]: 1 }}>
        <NeedsYou items={items} className="lg:col-span-2" />
        {project && wide && (
          <aside className="relative min-h-[460px] lg:row-span-3" aria-label="Ask the office">
            {/* Absolute so a long thread scrolls inside the card instead of stretching the grid rows beside it. */}
            <div className="absolute inset-0">
              <Compose key={project.id} projectId={project.id} variant="panel" />
            </div>
          </aside>
        )}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:col-span-2">
          {project && <Flow projectId={project.id} />}
          <Today today={data?.today} projectId={project?.id} />
        </div>
        {project && <LiveFeed projectId={project.id} className="lg:col-span-2" />}
      </div>

      <ReconnectItems />

      <Working />

      <ChiefTile />

      {project && !wide && <Compose key={project.id} projectId={project.id} variant="bar" />}
    </div>
  );
}

// ───────────────────────── needs you: one at a time ─────────────────────────

/** True while a modal dialog (the palette, a confirm) is open: Home's global keys must not act on the page behind it. */
export function modalOpen(doc: Pick<Document, "querySelector"> = document): boolean {
  return doc.querySelector('[role="dialog"][aria-modal="true"]') !== null;
}

function NeedsYou({ items, className }: { items: Item[]; className?: string }) {
  const [index, setIndex] = useState(0);
  const i = Math.min(index, Math.max(0, items.length - 1));
  const item = items[i];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (modalOpen()) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "ArrowRight" || e.key === "j") setIndex((x) => Math.min(items.length - 1, x + 1));
      else if (e.key === "ArrowLeft" || e.key === "k") setIndex((x) => Math.max(0, x - 1));
      else if (/^[1-9]$/.test(e.key)) document.querySelector<HTMLElement>(`[data-testid="focus-card"] [data-key="${e.key}"]`)?.click();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [items.length]);

  if (items.length === 0)
    return (
      <section className={cx(CARD, "flex min-h-[220px] flex-col", className)} aria-label="Needs you">
        <p className="text-[13px] text-ink-2">Needs you</p>
        <div className="flex flex-1 flex-col justify-center">
          <p className="text-[20px] font-medium tracking-[-0.02em]">Nothing needs you right now.</p>
          <p className="mt-1.5 text-[13.5px] text-ink-2">Decisions and things to keep an eye on show up here, one at a time.</p>
        </div>
      </section>
    );

  return (
    <section className={cx(CARD, "min-w-0", className)} aria-label="Needs you">
      <div className="mb-4 flex items-center justify-between text-[13px] text-ink-2">
        <span>Needs you</span>
        {items.length > 1 && (
          <span className="flex items-center gap-1">
            <button className="grid h-7 w-7 place-items-center rounded-md hover:bg-paper-2 disabled:opacity-30" onClick={() => setIndex(i - 1)} disabled={i === 0} aria-label="Previous">
              ‹
            </button>
            <span className="tabular-nums">
              {i + 1} of {items.length}
            </span>
            <button className="grid h-7 w-7 place-items-center rounded-md hover:bg-paper-2 disabled:opacity-30" onClick={() => setIndex(i + 1)} disabled={i === items.length - 1} aria-label="Next">
              ›
            </button>
          </span>
        )}
      </div>
      <div data-testid="focus-card" key={item!.kind === "decision" ? item!.d.id : `${item!.s.taskId}-${item!.s.reason}`}>
        {item!.kind === "decision" ? <DecisionBody d={item!.d} /> : <SteerBody s={item!.s} />}
      </div>
      {/* Testids the E2E suite keys on: one per verb with its count. */}
      <p className="sr-only">
        {(["act", "approve", "advise", "steer"] as const).map((v) => (
          <span key={v} data-testid={`needs-${v}`}>
            <span data-testid={`needs-${v}-count`}>{items.filter((x) => (x.kind === "steer" ? "steer" : verbKey(x.d)) === v).length}</span>
          </span>
        ))}
      </p>
    </section>
  );
}

/** One "Re-connect this agent's sandbox" item per active remote agent whose sandbox login is missing or expired. */
function ReconnectItems() {
  const agents = paletteActors(useActors().data ?? []).filter((a) => a.kind === "agent" && a.active && a.host === "vercel-sandbox");
  // `space-y-*` on the parent: a gate that renders nothing adds no margin, so no empty block is left behind.
  return (
    <div className="space-y-4 empty:hidden [&>*:first-child]:mt-12" data-testid="reconnect-items">
      {agents.map((a) => (
        <ReconnectGate key={a.id} actor={a} />
      ))}
    </div>
  );
}

function ReconnectGate({ actor }: { actor: ActorDTO }) {
  const status = useSandboxStatus(actor.id);
  return needsReconnect(status.data) ? <ReconnectItem actor={actor} sandboxName={status.data?.sandboxName ?? null} /> : null;
}

const verbKey = (d: DecisionDTO) => (d.kind === "human_action" ? "act" : d.kind === "question" ? "advise" : "approve");

export function DecisionBody({ d, canAnswer = true }: { d: DecisionDTO; canAnswer?: boolean }) {
  const name = useNames(useActors().data);
  const answer = useAnswer();
  const [pending, setPending] = useState<string | null>(null);
  const [why, setWhy] = useState("");
  const delegate = delegateBlockView(d);
  const submit = (optionId: string) => {
    if (NEEDS_RATIONALE.has(optionId) && !why.trim()) return setPending(optionId);
    answer.mutate({ id: d.id, optionId, rationale: why });
    setPending(null);
    setWhy("");
  };
  const asker = name(d.askedBy);
  return (
    <div data-testid="needs-a-human-item">
      <p className="flex flex-wrap items-center gap-x-2 text-[13px] text-ink-2">
        <span className="rounded-full bg-signal-soft px-2.5 py-0.5 text-[11.5px] font-medium text-[color:var(--medium)]">{VERB[d.kind] ?? "Advise"}</span>
        {asker && <span>from {asker}</span>}
        <span>· for {name(d.assigneeId) || "anyone"}</span>
        {d.taskId && (
          <Link to={`/tasks/${d.taskId}`} className="ml-auto text-ink-2 underline decoration-rule hover:text-ink">
            Open task
          </Link>
        )}
      </p>
      <div className="mt-4">
        <DecisionCrumb decision={d} />
      </div>
      <h2 className="mt-2 text-[22px] font-medium leading-snug tracking-[-0.01em] md:text-[24px]">{d.question}</h2>
      {d.blocking && d.priority.blockedMin > 0 && <p className="mt-2 text-[13px] text-ink-2">An agent has been waiting {formatMin(d.priority.blockedMin)} for this.</p>}
      {/* Delegation: a decision with no delegateId renders none of this, so it stays byte-identical to today. */}
      {d.delegateId && d.delegateState === "pending" && (
        <p className="mt-4 text-[13px] text-ink-2" data-testid="delegate-pending">
          {name(d.delegateId) || "Your agent"} is reviewing this. You can still answer it yourself.
        </p>
      )}
      {d.delegateId && d.delegateState === "failed" && (
        <p className="mt-4 text-[13px] text-high" data-testid="delegate-failed">
          {name(d.delegateId) || "Your agent"} could not review this.
        </p>
      )}
      {delegate && <DelegateBlock view={delegate} agentName={name(delegate.agentId)} personName={name(delegate.personId)} options={d.options} />}
      <ChiefNote note={d.chiefNote} className="mt-4" />
      <div className={cx("flex flex-col gap-2", d.chiefNote ? "mt-3" : "mt-6")}>
        {/* Viewers see the question but get no answer controls. */}
        {(canAnswer ? d.options : []).map((o, i) => {
          const rec = d.recommendation === o.id;
          return (
            <button
              key={o.id}
              data-key={i + 1}
              data-option={o.id}
              onClick={() => submit(o.id)}
              disabled={answer.isPending}
              className={cx(
                "group flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left text-[15px] transition-[background,border-color,transform] active:scale-[0.99] disabled:opacity-50",
                rec ? "border-ink bg-ink text-paper hover:opacity-90" : "border-rule hover:border-ink-3 hover:bg-paper-2",
              )}
            >
              <span className={cx("os-kbd shrink-0", rec && "border-transparent bg-paper/15 text-paper")}>{i + 1}</span>
              <span className="min-w-0 flex-1">
                {o.label}
              </span>
              {rec && <span className="text-[12px] opacity-70">Recommended</span>}
            </button>
          );
        })}
      </div>
      {canAnswer && pending && (
        <form
          className="mt-3 flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            submit(pending);
          }}
        >
          <input
            autoFocus
            className="flex-1 rounded-xl border border-rule bg-paper px-4 py-2.5 text-[14px] outline-none focus:border-ink-3"
            placeholder="A sentence on why (required for this answer)"
            value={why}
            onChange={(e) => setWhy(e.target.value)}
            aria-label="Rationale"
          />
          <button className="rounded-xl bg-ink px-4 text-[14px] text-paper disabled:opacity-40" disabled={!why.trim() || answer.isPending}>
            Send
          </button>
        </form>
      )}
      <ErrorNote error={answer.error} />
    </div>
  );
}

/**
 * The one-click confirm: the agent's recommendation above the (unchanged) numbered options, which
 * already mark the recommended one — so the existing number-key press is the whole interaction.
 * The header is the one label the spec calls non-negotiable: never a bare agent name.
 */
function DelegateBlock({ view, agentName, personName, options }: { view: DelegateBlockView; agentName: string; personName: string; options: DecisionDTO["options"] }) {
  const recommended = options.find((o) => o.id === view.recommendedOptionId);
  return (
    <div className="mt-4 rounded-xl border border-rule bg-paper-2 px-4 py-3 text-[13.5px]" data-testid="delegate-block">
      <p className="text-ink">
        <span className="font-medium">
          {agentName || "Your agent"} (agent) · delegated by {personName || "you"}
        </span>{" "}
        reviewed this
        {recommended && (
          <>
            {" "}
            · recommends <span className="font-medium">{recommended.label}</span>
          </>
        )}
      </p>
      {view.rationale && <p className="mt-1.5 text-ink-2">{view.rationale}</p>}
      {(view.critical.length > 0 || view.important.length > 0) && (
        <ul className="mt-2 space-y-1 text-ink-2">
          {view.critical.map((f, i) => (
            <li key={`critical-${i}`}>
              <span className="font-medium text-high">critical</span> — {f.text}
            </li>
          ))}
          {view.important.map((f, i) => (
            <li key={`important-${i}`}>
              <span className="font-medium text-[color:var(--medium)]">important</span> — {f.text}
            </li>
          ))}
        </ul>
      )}
      {view.minorCount > 0 && (
        <p className="mt-1.5 text-ink-3">
          +{view.minorCount} minor finding{view.minorCount === 1 ? "" : "s"}
        </p>
      )}
      <p className="mt-2 text-ink-3">Your click is needed: {view.whyLabel}.</p>
    </div>
  );
}

export function SteerBody({ s }: { s: SteerItem }) {
  const stop = useStopRun();
  return (
    <div data-testid="needs-a-human-item">
      <p className="flex items-center gap-2 text-[13px] text-ink-2">
        <span className="rounded-full bg-paper-2 px-2.5 py-0.5 text-[11.5px] font-medium">Steer</span>
        <span>
          #{s.taskNumber} {STEER_REASON_LABEL[s.reason]}
        </span>
      </p>
      <h2 className="mt-4 text-[22px] font-medium leading-snug tracking-[-0.01em]">{s.taskTitle}</h2>
      <p className="mt-2 line-clamp-3 text-[14px] text-ink-2">{stripAnsi(s.message)}</p>
      <div className="mt-6 flex flex-wrap gap-2">
        <Link to={`/tasks/${s.taskId}`} data-key="1" className="rounded-xl bg-ink px-4 py-2.5 text-[14px] text-paper hover:opacity-90">
          Open task
        </Link>
        {s.runId && (
          <button data-key="2" className="rounded-xl border border-rule px-4 py-2.5 text-[14px] text-high hover:bg-paper-2" onClick={() => stop.mutate(s.runId!)} disabled={stop.isPending}>
            Stop the run
          </button>
        )}
      </div>
      <ErrorNote error={stop.error} />
    </div>
  );
}

// ───────────────────────── working now ─────────────────────────

/** The mission card grid (components/MissionGrid.tsx) takes the place of the old "Working now" list. */
function Working() {
  return <MissionGrid renderDecision={(d, canAnswer) => <DecisionBody d={d} canAnswer={canAnswer} />} />;
}

// ───────────────────────── live: what the office just did ─────────────────────────

/** Events worth a line on the home screen: work starting, moving, shipping and people deciding. */
const FEED_TYPES = new Set(["task.created", "task.claimed", "task.stage_changed", "task.abandoned", "task.reverted", "task.merged", "decision.answered", "evidence.submitted", "run.started"]);

export function feedEvents(history: readonly EventDTO[], live: readonly EventDTO[], max = 7): EventDTO[] {
  const bySeq = new Map<number, EventDTO>();
  for (const e of [...history, ...live]) if (FEED_TYPES.has(e.type)) bySeq.set(e.seq, e);
  return [...bySeq.values()].sort((a, b) => b.seq - a.seq).slice(0, max);
}

function LiveFeed({ projectId, className }: { projectId: string; className?: string }) {
  const history = useEvents(projectId, 40);
  const { recent, connected } = useLive();
  const actors = useActors().data;
  const tasks = useProject(projectId).data?.tasks;
  const items = useMemo(() => feedEvents(history.data ?? [], recent.filter((e) => e.projectId === projectId), 6), [history.data, recent, projectId]);
  if (items.length === 0) return null;
  const taskOf = (e: EventDTO) => (e.subjectType === "task" ? tasks?.find((t) => t.id === e.subjectId) : undefined);
  return (
    <section className={cx(CARD, "min-w-0", className)} aria-label="Live" data-testid="live-feed">
      <p className="mb-2 flex items-center gap-2 text-[13px] text-ink-2">
        <span className={cx("h-1.5 w-1.5 rounded-full", connected ? "bg-[color:var(--ok)] shadow-[0_0_0_3px_color-mix(in_oklab,var(--ok)_20%,transparent)]" : "bg-ink-3")} />
        Live
      </p>
      <AnimatedList
        items={items.map((e) => ({ ...e, id: e.seq }))}
        renderItem={(e) => {
          const t = taskOf(e);
          return (
            <div className="flex min-w-0 items-center gap-3 border-b border-rule/40 py-2.5 last:border-b-0" data-testid="live-feed-item">
              <span className="min-w-0 flex-1 truncate text-[13.5px]">
                {t && (
                  <Link to={`/tasks/${t.id}`} className="font-medium hover:underline">
                    #{t.number}
                  </Link>
                )}
                <span className="text-ink-2"> {e.type === "run.started" ? `${historyName(actors, e.actorId) || "An agent"} started working` : describeEvent(e, (id) => historyName(actors, id))}</span>
              </span>
              <span className="shrink-0 text-[12px] tabular-nums text-ink-3">
                <Ago ts={e.ts} />
              </span>
            </div>
          );
        }}
      />
    </section>
  );
}

// ───────────────────────── Chief: one quiet line ─────────────────────────

/** "Chief: N actions today, M undone", linking to /chief (chief-of-office.md §8.3). Absent until the Chief has acted once. */
export function ChiefTile() {
  const feed = useChiefToday();
  if (!feed.data?.any) return null;
  return (
    <section className="os-rise mt-14" style={{ ["--i" as string]: 3 }} aria-label="Chief">
      <Link to="/chief" className="text-[13px] text-ink-2 hover:text-ink" data-testid="chief-tile">
        {chiefTileText(chiefToday(feed.data.actions, Date.now()))} <span className="text-ink-3">→</span>
      </Link>
    </section>
  );
}

// ───────────────────────── flow: one quiet bar ─────────────────────────

const FLOW: { stage: Stage; label: string; tone: string }[] = [
  { stage: "ready", label: "Ready", tone: "bg-ink-3/35" },
  { stage: "in_progress", label: "Building", tone: "bg-ink-2" },
  { stage: "blocked", label: "Waiting on you", tone: "bg-signal" },
  { stage: "verifying", label: "Checking", tone: "bg-ink-2/60" },
  { stage: "review", label: "In review", tone: "bg-signal/60" },
  { stage: "done", label: "Shipped", tone: "bg-ink" },
];

function Flow({ projectId }: { projectId: string }) {
  const tasks = useProject(projectId).data?.tasks ?? [];
  const name = (useProjects().data ?? []).find((p) => p.id === projectId)?.name ?? "All work";
  const counts = FLOW.map((f) => ({ ...f, n: tasks.filter((t) => t.stage === f.stage).length }));
  const total = counts.reduce((s, c) => s + c.n, 0);
  return (
    <section className={cx(CARD, "min-w-0")} aria-label="Work in flight">
      <Link to={`/projects/${projectId}`} className="mb-4 flex items-center justify-between text-[13px] text-ink-2 hover:text-ink">
        <span>{name}</span>
        <span className="text-ink-3 underline decoration-rule">Board</span>
      </Link>
      {total === 0 && <p className="text-[13.5px] text-ink-2">No tasks yet.</p>}
      <p className="mb-3 text-[28px] font-semibold leading-none tracking-[-0.03em] tabular-nums">
        <Ticker value={counts.find((c) => c.stage === "done")?.n ?? 0} /> <span className="text-[13px] font-normal tracking-normal text-ink-2">of {total} shipped</span>
      </p>
      <div className="flex h-2 overflow-hidden rounded-full bg-ink/[0.06]">
        {counts.map((c) => (c.n ? <span key={c.stage} className={cx("h-full", c.tone)} style={{ width: `${(c.n / total) * 100}%` }} title={`${c.label}: ${c.n}`} /> : null))}
      </div>
      <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-[12.5px] text-ink-2">
        {counts
          .filter((c) => c.n > 0)
          .map((c) => (
            <li key={c.stage} className="flex items-center gap-1.5">
              <span className={cx("h-2 w-2 rounded-full", c.tone)} />
              {c.label} <Ticker value={c.n} className="tabular-nums text-ink" />
            </li>
          ))}
      </ul>
    </section>
  );
}

// ───────────────────────── today: runs, spend, budget ─────────────────────────

function Today({ today, projectId }: { today: BridgeView["today"] | undefined; projectId: string | undefined }) {
  const budget = projectId ? budgetFor(today?.budgets, projectId) : null;
  return (
    <section className={cx(CARD, "min-w-0")} aria-label="Today" data-testid="bridge-today">
      <p className="mb-4 text-[13px] text-ink-2">Today</p>
      <p className="text-[28px] font-semibold leading-none tracking-[-0.03em] tabular-nums">
        <Ticker value={today?.runs ?? 0} /> <span className="text-[13px] font-normal tracking-normal text-ink-2">{today?.runs === 1 ? "run" : "runs"}</span>
      </p>
      <p className="mt-3 text-[13px] text-ink-2 tabular-nums">{today ? `${money(today.costUsd)} on ${today.nodes} ${today.nodes === 1 ? "machine" : "machines"}` : "…"}</p>
      {budget && (
        <p className={cx("mt-1 text-[12.5px]", BUDGET_TONE[budget.state])} data-testid="bridge-budget" data-budget-state={budget.state}>
          {BUDGET_STATE_LABEL[budget.state] && <>{BUDGET_STATE_LABEL[budget.state]} · </>}
          {budgetLine(budget)}
        </p>
      )}
    </section>
  );
}

// ───────────────────────── compose ─────────────────────────

/** The server links to /decisions/:id and /projects/:id/status; the app shows those on the Inbox and Status pages. */
export function homeBoxHref(href: string): string {
  if (href.startsWith("/decisions/")) return "/inbox";
  if (/^\/projects\/[^/]+\/status$/.test(href)) return "/status";
  return href;
}

type HomeBoxReply = {
  /** A question is accepted, not answered: the chat run answers later and the result is read with GET /home-box/:chatId. */
  chatId?: string;
  kind: "question" | "idea" | "unclear";
  answer?: { markdown: string; links: { label: string; href: string }[] };
  filed?: { specTask: { number: number }; specAgent: { name: string } | null };
};

/** One-click questions: the empty chat offers them, and after an answer the ones not yet asked come back as follow-ups. */
const SUGGESTIONS = ["What needs me today?", "Update me on the project", "What shipped today?"];

type Source = { label: string; href: string };
/** One turn of the Home chat. Office turns point at the question they answer, so dismissing an answer takes its question with it. */
type Turn =
  | { id: number; who: "you"; text: string }
  | { id: number; who: "office"; q: number; kind: "answer"; markdown: string; links: Source[]; by?: { fallback: boolean; runId: string | null } }
  | { id: number; who: "office"; q: number; kind: "filed"; text: string; error?: true }
  | { id: number; who: "office"; q: number; kind: "unclear"; line: string };

type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

const SOURCE_MARK = { task: "#", decision: "?", project: "◇", page: "→" } as const;

function OfficeMark() {
  return (
    <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full bg-ink/[0.06] text-ink-2" aria-hidden>
      <Symbol className="h-[8px] w-auto" />
    </span>
  );
}

function Sources({ links }: { links: Source[] }) {
  const [open, setOpen] = useState(true);
  if (links.length === 0) return null;
  return (
    <div className="mt-3 border-t border-rule/50 pt-2.5" data-testid="compose-sources">
      <button type="button" className="flex items-center gap-1.5 text-[12px] text-ink-3 hover:text-ink" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        Sources <span className="tabular-nums">{links.length}</span>
        <span className={cx("text-[9px] transition-transform", open ? "rotate-180" : "")} aria-hidden>
          ▾
        </span>
      </button>
      {open && (
        <ul className="mt-1.5 space-y-0.5">
          {links.map((l) => {
            const kind = sourceKind(l.href);
            return (
              <li key={l.href}>
                <Link to={homeBoxHref(l.href)} className="group -mx-2 flex min-w-0 items-center gap-2.5 rounded-xl px-2 py-1.5 hover:bg-ink/[0.04]" data-testid="compose-answer-link">
                  <span className="grid h-5 w-5 shrink-0 place-items-center rounded-md bg-ink/[0.06] text-[10px] text-ink-2" aria-hidden>
                    {SOURCE_MARK[kind]}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-[12.5px] text-ink group-hover:underline">{l.label}</span>
                  <span className="shrink-0 text-[11px] text-ink-3">{kind}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

const stripLinks = (md: string) => md.replace(/\[([^\]]*)\]\([^)]*\)/g, "$1");

/** Quiet line under an answer: which agent and model wrote it, or "quick summary" when no model could. */
function AnsweredBy({ by }: { by: { fallback: boolean; runId: string | null } }) {
  const run = useRun(by.runId ?? "", !by.fallback && !!by.runId);
  const actors = useActors();
  const name = useNames(actors.data);
  if (!by.fallback && !by.runId) return null;
  if (!by.fallback && !run.data) return null;
  const r = run.data?.run;
  return (
    <p className="mt-2 text-[11.5px] text-ink-3" data-testid="compose-answered-by">
      {answeredByLine({ fallback: by.fallback, agent: name(r?.agentId), model: r?.model ?? null })}
    </p>
  );
}

function filedText(filed: NonNullable<HomeBoxReply["filed"]>): string {
  return filed.specAgent
    ? `Filed as an idea. ${filed.specAgent.name} is writing the spec (task #${filed.specTask.number}); you'll be asked only what it can't decide.`
    : `Filed as an idea (task #${filed.specTask.number}). It's waiting for someone to write the spec.`;
}

function Thinking() {
  return (
    <div className="flex items-center gap-3" data-testid="compose-thinking" role="status">
      <OfficeMark />
      <span className="text-[13px] text-ink-2">Thinking</span>
      <span className="flex gap-1" aria-hidden>
        {[0, 1, 2].map((i) => (
          <span key={i} className="h-1.5 w-1.5 animate-pulse rounded-full bg-ink-3" style={{ animationDelay: `${i * 160}ms` }} />
        ))}
      </span>
    </div>
  );
}

/**
 * The Home box: ask about the office (answered in place, nothing filed) or describe something to build (an idea goes to spec-agent).
 * `panel` is a chat card on wide screens that keeps the conversation as a thread; `bar` floats at the bottom of narrow ones and shows
 * only the latest reply.
 */
function Compose({ projectId, variant }: { projectId: string; variant: "panel" | "bar" }) {
  const me = useMe().data!;
  const [text, setText] = useState("");
  const [turns, setTurns] = useState<Turn[]>([]);
  const nextId = useRef(1);
  const scroller = useRef<HTMLDivElement>(null);
  /** Bumped by Clear: a reply to a question asked before it is dropped. */
  const generation = useRef(0);
  const qc = useQueryClient();
  const live = useLive();
  const send = useCreate<HomeBoxReply>("/home-box", [["intent"], ["project"], ["bridge"], ["overview"]]);
  // The question being answered by a chat run: Thinking stays until it answers. homebox.updated refetches this query at once; the poll is the fallback.
  // `answer` is set when the person chose "Answer it": whatever the model calls the line, it is shown as an answer.
  const [chat, setChat] = useState<{ id: string; q: number; answer: boolean } | null>(null);
  const chatQuery = useQuery({
    enabled: !!chat,
    queryKey: ["homebox", chat?.id ?? ""],
    queryFn: () => api<HomeChat>("GET", `/home-box/${chat!.id}`),
    refetchInterval: homeChatPollMs(live.connected),
  });
  const busy = send.isPending || chat != null;
  const canFile = me.kind === "human" && me.access !== "viewer";
  // Live: the filed line says the work waits whenever the banner on this page says no agent can work.
  const waiting = useNoWorkerReason() != null;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (modalOpen()) return;
      const t = e.target as HTMLElement | null;
      if (e.key === "/" && !(t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA"))) {
        e.preventDefault();
        document.querySelector<HTMLInputElement>('[data-testid="compose-input"]')?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const el = scroller.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns.length, busy]);

  const settled = chatQuery.data;
  const finished = chat && settled && settled.id === chat.id && (settled.status === "answered" || settled.status === "failed") ? settled : null;
  const done = finished != null;
  // The request can't be read any more (gone, or the connection failed after its retries): stop waiting instead of thinking forever.
  // Whether an earlier poll cached data does not matter: the query is in error after its retries, or on a 4xx.
  const lost = chatQuery.isError && !done;
  useEffect(() => {
    if (!chat) return;
    if (!done && !lost) return;
    const { q } = chat;
    const oops = "Something went wrong: the office sent no answer. Try again.";
    if (finished?.filed) {
      // The chat run filed an idea after the 202: the lists the POST would have refreshed are stale.
      for (const root of ["intent", "project", "bridge", "overview"]) void qc.invalidateQueries({ queryKey: [root] });
    }
    setTurns((list) => {
      const line = list.find((t) => t.who === "you" && t.id === q);
      const turn = (rest: DistributiveOmit<Extract<Turn, { who: "office" }>, "id" | "who" | "q">): Turn => ({ id: nextId.current++, who: "office", q, ...rest });
      const reply: Turn =
        !finished || finished.status === "failed"
          ? turn({ kind: "filed", text: oops, error: true })
          : finished.filed
            ? turn({ kind: "filed", text: filedText(finished.filed) })
            : finished.kind === "unclear" && !chat.answer && line?.who === "you"
              ? turn({ kind: "unclear", line: line.text })
              : (finished.answer ?? "").trim() === ""
                ? turn({ kind: "filed", text: oops, error: true })
                : turn({ kind: "answer", markdown: stripLinks(finished.answer ?? ""), links: finished.links, by: { fallback: finished.fallback, runId: finished.runId } });
      return [...list, reply];
    });
    setChat(null);
  }, [chat, done, lost, finished, qc]);

  if (!canFile) return null;
  const id = () => nextId.current++;
  const dropUnclear = (list: Turn[]) => list.filter((t) => !(t.who === "office" && t.kind === "unclear"));
  /** Removes an office turn and the question it answered. */
  const dismiss = (turn: Turn) => setTurns((list) => list.filter((t) => t.id !== turn.id && !(turn.who === "office" && t.id === turn.q)));

  const run = (line: string, as?: "answer" | "idea") => {
    if (busy) return;
    // A choice on an unclear line answers the question already in the thread; anything else is a new question.
    const asked = as ? [...turns].reverse().find((t) => t.who === "you" && t.text === line) : undefined;
    const q = asked?.id ?? id();
    // Without the model the hub filed ideas and asked about unclear lines; with it every line without `as` is a chat. The same classifier
    // runs here first, so an idea is filed and an unclear line asks "answer or start work?" whether or not a model answers.
    // Only a clear-cut line is decided here. A follow-up that opens with a conjunction ("and show me the blocked tasks") and a loose
    // description with no work verb ("describes something without asking") go to the chat run, which can tell a conversation from work.
    const verdict = classifyHomeBoxText(line);
    const followUp = /^\s*(and|also|but|so|then|plus|ok|okay|thanks|thank you|וגם|גם|אז|אבל|טוב|אוקיי|תודה)(?=[\s,.!?]|$)/i.test(line);
    const clearIdea = verdict.kind === "idea" && verdict.reason === "starts like a piece of work";
    const picked = as ?? (followUp ? undefined : clearIdea ? "idea" : verdict.kind === "unclear" ? "unclear" : undefined);
    setTurns((list) => (asked ? dropUnclear(list) : [...dropUnclear(list), { id: q, who: "you", text: line }]));
    setText("");
    if (picked === "unclear") {
      setTurns((list) => [...list, { id: id(), who: "office", q, kind: "unclear", line }]);
      return;
    }
    // The turns before this question, not counting the question itself or an unclear prompt. Only what was said: questions and answers.
    // The "Filed as an idea" and error lines are interface text, not conversation. The hub trims to the last turns.
    const history = trimChatHistory(
      dropUnclear(turns).flatMap((t) => (t.id === q ? [] : t.who === "you" ? [{ role: "you", text: t.text }] : t.kind === "answer" ? [{ role: "office", text: t.markdown }] : [])),
    );
    const gen = generation.current;
    // "Answer it" goes through the chat run too (no `as`): `as: "answer"` would only give the canned status summary.
    const body = picked === "idea" ? { projectId, text: line, as: "idea" } : { projectId, text: line, history };
    send.mutate(body, {
      onSuccess: (r) => {
        if (gen !== generation.current) return; // cleared while the request was in flight
        // Accepted, not answered: the Thinking row stays until the chat run answers.
        if (r.chatId) {
          setChat({ id: r.chatId, q, answer: as === "answer" });
          return;
        }
        const reply: Turn =
          r.kind === "unclear"
            ? { id: id(), who: "office", q, kind: "unclear", line }
            : r.kind === "question" && r.answer
              ? { id: id(), who: "office", q, kind: "answer", markdown: stripLinks(r.answer.markdown), links: r.answer.links, by: { fallback: true, runId: null } }
              : { id: id(), who: "office", q, kind: "filed", text: r.filed ? filedText(r.filed) : "Something went wrong: the office sent no answer. Try again.", ...(r.filed ? {} : { error: true as const }) };
        setTurns((list) => [...list, reply]);
      },
    });
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (text.trim().length < 3) return;
    run(text.trim());
  };

  const asked = new Set(turns.filter((t) => t.who === "you").map((t) => (t as { text: string }).text));
  const last = turns.at(-1);
  const followUps = last?.who === "office" && last.kind === "answer" && !busy ? SUGGESTIONS.filter((s) => !asked.has(s)).slice(0, 2) : [];

  const officeTurn = (t: Extract<Turn, { who: "office" }>, inBar: boolean) => {
    if (t.kind === "answer")
      return (
        <div key={t.id} className={cx("relative rounded-[18px] px-4 py-3 text-[13.5px] text-ink-2", inBar ? "glass max-h-[40vh] overflow-y-auto" : "min-w-0 flex-1 bg-ink/[0.035]")} data-testid="compose-answer">
          <button type="button" className="absolute right-3 top-2.5 text-[12px] text-ink-3 hover:text-ink" onClick={() => dismiss(t)} aria-label="Dismiss answer" data-testid="compose-answer-close">
            ✕
          </button>
          <div className="pr-5">
            <Markdown text={t.markdown} />
          </div>
          <Sources links={t.links} />
          {t.by && <AnsweredBy by={t.by} />}
        </div>
      );
    if (t.kind === "filed")
      return (
        <p key={t.id} className={cx("rounded-[18px] px-4 py-3 text-[13px] text-ink-2", inBar ? "glass text-center" : "min-w-0 flex-1 bg-[color:var(--ok)]/[0.08]")} data-testid="compose-filed">
          {waiting && !t.error ? "Saved. The spec is waiting until an agent can work — you'll be asked only what it can't decide." : t.text}
        </p>
      );
    return (
      <div key={t.id} className={cx("rounded-[18px] px-4 py-3 text-[13px] text-ink-2", inBar ? "glass flex flex-wrap items-center justify-center gap-3" : "min-w-0 flex-1 bg-ink/[0.035]")} data-testid="compose-unclear">
        <p>Should I answer this, or start work on it?</p>
        <div className="mt-2 flex flex-wrap gap-2">
          <button type="button" className="rounded-full bg-ink px-3.5 py-1 text-paper hover:opacity-90" disabled={busy} onClick={() => run(t.line, "answer")} data-testid="compose-choose-answer">
            Answer it
          </button>
          <button type="button" className="rounded-full bg-ink/[0.07] px-3.5 py-1 text-ink hover:bg-ink/[0.12]" disabled={busy} onClick={() => run(t.line, "idea")} data-testid="compose-choose-idea">
            Start work on it
          </button>
        </div>
      </div>
    );
  };

  const input = (
    <div className={cx("flex items-center gap-2", variant === "bar" ? "glass rounded-[20px] py-2.5 pl-5 pr-2.5 focus-within:ring-2 focus-within:ring-[color:var(--focus)]" : "rounded-[16px] border border-rule/60 bg-paper/60 py-2 pl-4 pr-2 transition-colors focus-within:border-[color:var(--focus)]")}>
      <input
        className="min-w-0 flex-1 bg-transparent text-[15px] outline-none placeholder:text-ink-3"
        // The field itself shows no ring; its rounded box shows focus instead (the global :focus-visible outline would draw a square inside it).
        style={{ outline: "none" }}
        placeholder="Ask about the office, or describe something to build"
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setTurns(dropUnclear);
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape" && last?.who === "office") dismiss(last);
        }}
        readOnly={busy}
        aria-label="Ask about the office, or describe something to build"
        data-testid="compose-input"
      />
      <kbd className="os-kbd hidden sm:inline">/</kbd>
      <button
        type="submit"
        className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-ink text-[14px] text-paper transition-opacity disabled:opacity-25"
        disabled={busy || text.trim().length < 3}
        aria-label="Send"
        data-testid="compose-send"
      >
        ↑
      </button>
    </div>
  );

  if (variant === "bar") {
    const latest = last?.who === "office" ? last : null;
    return (
      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-20 px-4 pb-5">
        <form onSubmit={submit} className="pointer-events-auto mx-auto max-w-[920px]" data-testid="os-shell">
          <div aria-live="polite" className="mb-2 empty:mb-0">
            {busy ? <div className="glass rounded-[18px] px-4 py-3"><Thinking /></div> : latest && officeTurn(latest, true)}
          </div>
          {input}
          <ErrorNote error={send.error} />
        </form>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className={cx(CARD, "flex h-full flex-col")} data-testid="os-shell">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="shrink-0 text-[15px] font-medium">Ask the office</h2>
        {turns.length > 0 && (
          <button type="button" className="text-[12px] text-ink-3 hover:text-ink" onClick={() => {
              setTurns([]);
              setChat(null);
              generation.current++;
            }} data-testid="compose-clear">
            Clear
          </button>
        )}
      </div>
      <div ref={scroller} className="-mx-1 mt-4 min-h-0 flex-1 overflow-y-auto px-1" aria-live="polite">
        {turns.length === 0 && !busy ? (
          <div className="flex h-full flex-col justify-center pb-4" data-testid="compose-suggestions">
            <span className="grid h-11 w-11 place-items-center rounded-2xl bg-ink/[0.05] text-ink-2" aria-hidden>
              <Symbol className="h-[11px] w-auto" />
            </span>
            <p className="mt-4 text-[18px] font-medium tracking-[-0.02em]">What do you want to know?</p>
            <p className="mt-1 text-[13px] leading-relaxed text-ink-2">Ask about tasks, agents and decisions, or describe something to build and it becomes an idea.</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {SUGGESTIONS.map((q) => (
                <button key={q} type="button" className="rounded-full border border-rule/70 px-3.5 py-1.5 text-[13px] text-ink-2 hover:border-ink/30 hover:text-ink" onClick={() => run(q)} data-testid="compose-suggestion">
                  {q}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-3 pb-1">
            {turns.map((t) =>
              t.who === "you" ? (
                <div key={t.id} className="flex justify-end">
                  <p className="max-w-[85%] rounded-[18px] rounded-br-md bg-ink px-4 py-2 text-[13.5px] text-paper" data-testid="compose-question">
                    {t.text}
                  </p>
                </div>
              ) : (
                <div key={t.id} className="flex items-start gap-2.5">
                  <OfficeMark />
                  {officeTurn(t, false)}
                </div>
              ),
            )}
            {busy && <Thinking />}
            {followUps.length > 0 && (
              <div className="flex flex-wrap gap-2 pl-[38px]" data-testid="compose-followups">
                {followUps.map((q, i) => (
                  <button key={q} type="button" className="flex items-center gap-2 rounded-full border border-rule/70 py-1 pl-1.5 pr-3 text-[12.5px] text-ink-2 hover:border-ink/30 hover:text-ink" onClick={() => run(q)} data-testid="compose-followup">
                    <span className="grid h-5 w-5 place-items-center rounded-full bg-ink/[0.06] text-[11px] tabular-nums">{i + 1}</span>
                    {q}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <div className="mt-3">{input}</div>
      <ErrorNote error={send.error} />
    </form>
  );
}
